import React, { useMemo } from 'react';

import { Formik } from 'formik';

import { useRadioReportContext } from './RadioReportContext';

import Button from 'CommonComponents/Button';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';

const SCOPE_CENTRAL = 'central';
const SCOPE_VENUE = 'venue';

const DETAIL_SUMMARY = 'summary';
const DETAIL_DETAILED = 'detailed';

const selectClassName =
    'tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal';

const ragBasisOptions = () => [
    { value: 'gross', label: $t('Gross') },
    { value: 'residual', label: $t('Residual') },
];

// IB-681 output formats. Register = the default table/summary view.
const formatOptions = () => [
    { value: 'register', label: $t('Register') },
    { value: 'rag_matrix', label: $t('RAG Matrix') },
    { value: 'weighted_value', label: $t('Weighted Value') },
];

const verifiedOptions = () => [
    { value: '', label: $t('Any') },
    { value: 'verified', label: $t('Verified') },
    { value: 'unverified', label: $t('Unverified') },
];

const actionStatusOptions = () => [
    { value: 'all', label: $t('All') },
    { value: 'has_overdue', label: $t('Has overdue action') },
    { value: 'none', label: $t('No actions') },
    { value: 'all_complete', label: $t('All actions complete') },
];

// Map the verified select value ('' | 'verified' | 'unverified') to the
// tri-state the backend expects (null | true | false).
const verifiedToValue = (raw) => {
    if (raw === 'verified') return true;
    if (raw === 'unverified') return false;
    return null;
};

const verifiedFromValue = (value) => {
    if (value === true) return 'verified';
    if (value === false) return 'unverified';
    return '';
};

/**
 * The RADIO report builder controls (IB-678).
 *
 * A type-agnostic form: an item-type multiselect drives which of the five RADIO
 * types are included, the dual-lens scope selector filters by venue /
 * department / cost-centre / central, and every remaining control is a filter
 * that narrows only the types that own the underlying field (decision #2).
 * Submitting pushes the assembled criteria into the report context, which
 * re-fetches the on-screen data.
 */
const RadioReportCriteria = () => {
    const { criteria, options, updateCriteria } = useRadioReportContext();

    const initialValues = useMemo(() => ({ ...criteria }), []);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                updateCriteria(values);
                actions.setSubmitting(false);
            }}
        >
            {({ values, isSubmitting, handleSubmit, setFieldValue }) => {
                const isCentral = values.scope_level === SCOPE_CENTRAL;

                return (
                    <div className="tw-px-6 tw-py-7 tw-space-y-6 tw-rounded-lg tw-bg-background">
                        {/* Item types + scope */}
                        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                            <Field label={$t('Item Types')}>
                                <MultiSelectField
                                    className={selectClassName}
                                    options={options.itemType}
                                    value={values.item_types}
                                    backgroundColor="#e4e4e480"
                                    isDisabled={isSubmitting}
                                    hideSelectedOptions={false}
                                    clearable
                                    placeholder={$t('All types')}
                                    onChange={(selected) => setFieldValue('item_types', toValues(selected))}
                                />
                            </Field>

                            <Field label={$t('Scope')}>
                                <SelectField
                                    className={selectClassName}
                                    options={options.scope}
                                    value={values.scope_level}
                                    backgroundColor="#e4e4e480"
                                    isDisabled={isSubmitting}
                                    placeholder={$t('Select scope')}
                                    onChange={(value) => {
                                        setFieldValue('scope_level', value ? value.value : SCOPE_CENTRAL);
                                        setFieldValue('scope_entity_ids', []);
                                    }}
                                />
                            </Field>

                            {!isCentral && (
                                <Field label={$t('Scope entities')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={(options.scopeEntitiesByLevel && options.scopeEntitiesByLevel[values.scope_level]) || []}
                                        value={values.scope_entity_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All available')}
                                        onChange={(selected) => setFieldValue('scope_entity_ids', toValues(selected))}
                                    />
                                </Field>
                            )}

                            {/* Only the Venue lens actually consults include_central — the
                                Department/Cost-Centre branches of RadioReportService::buildScope
                                hardcode includeCentral: false regardless of this flag (they
                                narrow by department/cost-centre membership, not by venue), so
                                the checkbox would otherwise render but do nothing. */}
                            {values.scope_level === SCOPE_VENUE && (
                                <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 lg:tw-self-end lg:tw-h-9">
                                    <input
                                        type="checkbox"
                                        disabled={isSubmitting}
                                        checked={!!values.include_central}
                                        onChange={() => setFieldValue('include_central', !values.include_central)}
                                    />
                                    {$t('Include central / cross-cutting items')}
                                </label>
                            )}
                        </div>

                        {/* Filters */}
                        <div>
                            <label className="text-input__label tw-mb-2 tw-block">{$t('Filters')}</label>
                            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                                <Field label={$t('Statuses')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.status}
                                        value={values.statuses}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All statuses')}
                                        onChange={(selected) => setFieldValue('statuses', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('RAG Basis')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={ragBasisOptions()}
                                        value={values.rag_basis}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('rag_basis', value ? value.value : 'gross')}
                                    />
                                </Field>

                                <Field label={$t('Priority RAG')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.ragClassification}
                                        value={values.rag_classifications}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All RAG')}
                                        onChange={(selected) => setFieldValue('rag_classifications', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Priority')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.priority}
                                        value={values.priorities}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All priorities')}
                                        onChange={(selected) => setFieldValue('priorities', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Impact Categories')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.impactCategory}
                                        value={values.impact_category_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All categories')}
                                        onChange={(selected) => setFieldValue('impact_category_ids', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Risk Types')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.riskType}
                                        value={values.risk_type_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All risk types')}
                                        onChange={(selected) => setFieldValue('risk_type_ids', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Owners')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.owner}
                                        value={values.owner_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All owners')}
                                        onChange={(selected) => setFieldValue('owner_ids', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Assumptions verified')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={verifiedOptions()}
                                        value={verifiedFromValue(values.verified)}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('verified', verifiedToValue(value ? value.value : ''))}
                                    />
                                </Field>

                                <Field label={$t('Action status')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={actionStatusOptions()}
                                        value={values.action_status}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('action_status', value ? value.value : 'all')}
                                    />
                                </Field>

                                <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 lg:tw-self-end lg:tw-h-9">
                                    <input
                                        type="checkbox"
                                        disabled={isSubmitting}
                                        checked={!!values.afc_only}
                                        onChange={() => setFieldValue('afc_only', !values.afc_only)}
                                    />
                                    {$t('AFC only')}
                                </label>
                            </div>
                        </div>

                        {/* Structure */}
                        <div>
                            <label className="text-input__label tw-mb-2 tw-block">{$t('Structure')}</label>
                            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                                <Field label={$t('Format')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={formatOptions()}
                                        value={values.format}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('format', value ? value.value : 'register')}
                                    />
                                </Field>

                                <Field label={$t('Group By')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={options.groupBy}
                                        value={values.group_by}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('group_by', value ? value.value : 'type')}
                                    />
                                </Field>

                                <Field label={$t('Detail Level')}>
                                    <div className="tw-h-9 tw-flex tw-items-center tw-gap-x-5">
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                            <input
                                                type="radio"
                                                name="detail_level"
                                                disabled={isSubmitting}
                                                value={DETAIL_SUMMARY}
                                                checked={values.detail_level === DETAIL_SUMMARY}
                                                onChange={() => setFieldValue('detail_level', DETAIL_SUMMARY)}
                                            />
                                            {$t('Summary')}
                                        </label>
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                            <input
                                                type="radio"
                                                name="detail_level"
                                                disabled={isSubmitting}
                                                value={DETAIL_DETAILED}
                                                checked={values.detail_level === DETAIL_DETAILED}
                                                onChange={() => setFieldValue('detail_level', DETAIL_DETAILED)}
                                            />
                                            {$t('Detailed')}
                                        </label>
                                    </div>
                                </Field>

                                <Field label={$t('Visible Columns')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.column}
                                        value={values.visible_columns || []}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting || values.detail_level === DETAIL_SUMMARY}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All columns')}
                                        onChange={(selected) => setFieldValue('visible_columns', selected && selected.length ? toValues(selected) : null)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="tw-flex tw-justify-end">
                            <Button
                                className="disabled:tw-bg-blue-25"
                                variant="primary"
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                loading={isSubmitting}
                            >
                                {$t('Apply')}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
};

/**
 * A labelled form control wrapper — keeps the label + control spacing
 * consistent with the sibling criteria forms.
 */
const Field = ({ label, children }) => (
    <div className="tw-flex tw-flex-col tw-space-y-1.5">
        <label className="text-input__label">{label}</label>
        {children}
    </div>
);

// react-select hands back an array of { value, label }; pull the values out.
const toValues = (selected) => (selected || []).map((option) => option.value);

export default RadioReportCriteria;
